import { createClientLogger, logClientError } from '@/client/logging/client-logger';
import { isRequestCanceled } from '@/client/error-handling/is-request-canceled';
import { useCallback, useEffect, useRef, useState } from 'react';

type GroupEditorTargetBase = {
    id: string;
    name: string;
};

type GroupEditorDataBase = {
    id: string;
    name: string;
};

type UseGroupEditorModalControllerArgs<TGroupEditorData extends GroupEditorDataBase> = {
    loadGroupEditorData: (groupId: string, signal?: AbortSignal) => Promise<TGroupEditorData>;
    resetSelectionState: () => void;
};

const groupEditorModalLogger = createClientLogger({
    scope: 'group-editor-modal',
});

export const useGroupEditorModalController = <
    TGroupEditorTarget extends GroupEditorTargetBase,
    TGroupEditorData extends GroupEditorDataBase,
>({
    loadGroupEditorData,
    resetSelectionState,
}: UseGroupEditorModalControllerArgs<TGroupEditorData>) => {
    const [openedGroupEditor, setOpenedGroupEditor] = useState<TGroupEditorTarget | null>(null);
    const [groupEditorData, setGroupEditorData] = useState<TGroupEditorData | null>(null);
    const [groupEditorNameDraft, setGroupEditorNameDraft] = useState('');
    const [isGroupEditorLoading, setIsGroupEditorLoading] = useState(false);
    const [groupEditorError, setGroupEditorError] = useState<string | null>(null);
    const [isGroupEditorNameSaving, setIsGroupEditorNameSaving] = useState(false);
    const [isGroupEditorUngrouping, setIsGroupEditorUngrouping] = useState(false);
    const groupEditorAbortController = useRef<AbortController | null>(null);

    const abortGroupEditorRequest = useCallback(() => {
        if (groupEditorAbortController.current) {
            groupEditorAbortController.current.abort();
            groupEditorAbortController.current = null;
        }
    }, []);

    useEffect(() => {
        return () => {
            abortGroupEditorRequest();
        };
    }, [abortGroupEditorRequest]);

    const onGroupEditorClose = useCallback(() => {
        abortGroupEditorRequest();
        resetSelectionState();
        setOpenedGroupEditor(null);
        setGroupEditorData(null);
        setGroupEditorNameDraft('');
        setGroupEditorError(null);
        setIsGroupEditorLoading(false);
        setIsGroupEditorNameSaving(false);
        setIsGroupEditorUngrouping(false);
    }, [abortGroupEditorRequest, resetSelectionState]);

    const onGroupEditorModalVisibilityChange = useCallback(
        (isShown: boolean) => {
            if (!isShown) {
                onGroupEditorClose();
            }
        },
        [onGroupEditorClose],
    );

    const onGroupEditorOpen = useCallback(
        (groupEditorTarget: TGroupEditorTarget) => {
            abortGroupEditorRequest();

            const abortController = new AbortController();
            groupEditorAbortController.current = abortController;

            setOpenedGroupEditor(groupEditorTarget);
            setGroupEditorData(null);
            setGroupEditorNameDraft(groupEditorTarget.name);
            setGroupEditorError(null);
            resetSelectionState();
            setIsGroupEditorNameSaving(false);
            setIsGroupEditorUngrouping(false);
            setIsGroupEditorLoading(true);

            void loadGroupEditorData(groupEditorTarget.id, abortController.signal)
                .then((data) => {
                    setGroupEditorData(data);
                    setGroupEditorNameDraft(data.name);
                })
                .catch((error) => {
                    if (isRequestCanceled(error)) {
                        return;
                    }

                    logClientError({
                        logger: groupEditorModalLogger,
                        message: '[client.group-management.data-load] Failed to load group editor data.',
                        error,
                        context: {
                            groupId: groupEditorTarget.id,
                        },
                    });
                    setGroupEditorError('Не удалось загрузить данные группы.');
                })
                .finally(() => {
                    if (groupEditorAbortController.current === abortController) {
                        groupEditorAbortController.current = null;
                        setIsGroupEditorLoading(false);
                    }
                });
        },
        [abortGroupEditorRequest, loadGroupEditorData, resetSelectionState],
    );

    const normalizedGroupEditorNameDraft = groupEditorNameDraft.trim();
    const hasGroupEditorNameChanged = groupEditorData
        ? normalizedGroupEditorNameDraft !== groupEditorData.name.trim()
        : false;

    return {
        openedGroupEditor,
        setOpenedGroupEditor,
        groupEditorData,
        setGroupEditorData,
        groupEditorNameDraft,
        setGroupEditorNameDraft,
        isGroupEditorLoading,
        groupEditorError,
        isGroupEditorNameSaving,
        setIsGroupEditorNameSaving,
        isGroupEditorUngrouping,
        setIsGroupEditorUngrouping,
        normalizedGroupEditorNameDraft,
        hasGroupEditorNameChanged,
        onGroupEditorOpen,
        onGroupEditorClose,
        onGroupEditorModalVisibilityChange,
    };
};
